// Thin wrappers around /api/recordings/*. Per-call addressing — one session
// may have many call recordings, each independently listable / playable /
// deletable. Keeps URL + auth header logic out of the UI components.

import type { SessionId } from '@prakash-software/shared';
import { apiFetch } from './api';
import { useAgentStore } from '../stores/agent';
import { useRecordingsStore } from '../stores/recordings';
import { serverOrigin as serverBase } from './server-origin';

export interface CallRecording {
  callId: string;
  chunkCount: number;
  totalBytes: number;
  /** ms since epoch — first chunk's recorded-at. */
  startedAt: number;
  lastUploadAt: number;
}

export interface ChunkSummary {
  callId: string;
  seq: number;
  byteSize: number;
}

/** List every call recording attached to a session, newest first. */
export async function listCallRecordings(
  sessionId: SessionId,
): Promise<CallRecording[]> {
  const res = await apiFetch<{ calls: CallRecording[] }>(
    `/api/recordings/${encodeURIComponent(sessionId)}/calls`,
  );
  return res.calls ?? [];
}

export async function listSessionChunks(sessionId: SessionId): Promise<ChunkSummary[]> {
  const res = await apiFetch<{ chunks: ChunkSummary[] }>(
    `/api/recordings/${encodeURIComponent(sessionId)}/chunks`,
  );
  return res.chunks ?? [];
}

async function fetchChunkBytes(
  sessionId: SessionId,
  callId: string,
  seq: number,
): Promise<ArrayBuffer> {
  const token = useAgentStore.getState().tokens?.accessToken;
  const res = await fetch(
    `${serverBase()}/api/recordings/${encodeURIComponent(sessionId)}` +
      `/calls/${encodeURIComponent(callId)}/chunks/${seq}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    throw Object.assign(new Error('chunk-failed'), { status: res.status });
  }
  return await res.arrayBuffer();
}

async function fetchCallPlaybackBlobUrl(
  sessionId: SessionId,
  callId: string,
): Promise<string> {
  const token = useAgentStore.getState().tokens?.accessToken;
  const res = await fetch(
    `${serverBase()}/api/recordings/${encodeURIComponent(sessionId)}` +
      `/calls/${encodeURIComponent(callId)}/playback`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    throw Object.assign(new Error('playback-failed'), { status: res.status });
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Attach a chunked WebM stream for ONE call to the given <video> via
 * MediaSource. Each chunk goes through appendBuffer in order so the browser
 * treats the whole thing as a live-ish stream and plays past chunk 0's
 * declared duration. Returns the URL so callers can revoke it on unmount.
 */
export async function attachCallMSEPlayer(
  video: HTMLVideoElement,
  sessionId: SessionId,
  callId: string,
): Promise<string> {
  if (typeof MediaSource === 'undefined') {
    return fetchCallPlaybackBlobUrl(sessionId, callId);
  }
  const candidates = [
    'video/webm;codecs="vp9,opus"',
    'video/webm;codecs="vp8,opus"',
    'video/webm',
  ];
  const mime = candidates.find((m) => MediaSource.isTypeSupported(m));
  if (!mime) {
    return fetchCallPlaybackBlobUrl(sessionId, callId);
  }

  const allChunks = await listSessionChunks(sessionId);
  const chunks = allChunks
    .filter((c) => c.callId === callId)
    .sort((a, b) => a.seq - b.seq);
  if (chunks.length === 0) {
    throw Object.assign(new Error('no-chunks'), { status: 404 });
  }

  const ms = new MediaSource();
  const url = URL.createObjectURL(ms);
  video.src = url;

  ms.addEventListener('sourceopen', () => {
    try {
      ms.duration = Number.POSITIVE_INFINITY;
    } catch {
      /* some browsers reject; ignore */
    }
    let sourceBuffer: SourceBuffer;
    try {
      sourceBuffer = ms.addSourceBuffer(mime);
    } catch (err) {
       
      console.warn('[recording] addSourceBuffer failed', err);
      try {
        ms.endOfStream('decode');
      } catch {
        /* noop */
      }
      return;
    }
    let i = 0;
    function pumpNext(): void {
      if (i >= chunks.length) {
        try {
          ms.endOfStream();
        } catch {
          /* may already be ended */
        }
        return;
      }
      const seq = chunks[i]!.seq;
      i++;
      fetchChunkBytes(sessionId, callId, seq)
        .then((bytes) => sourceBuffer.appendBuffer(bytes))
        .catch((err) => {
           
          console.warn('[recording] chunk fetch failed', seq, err);
          pumpNext();
        });
    }
    sourceBuffer.addEventListener('updateend', pumpNext);
    sourceBuffer.addEventListener('error', () => {
       
      console.warn('[recording] SourceBuffer error');
    });
    pumpNext();
  });

  return url;
}

export interface TranscriptDonePayload {
  callId: string;
  body: string;
  provider: string;
  language: string | null;
  createdAt: number;
}
export type TranscribeJobState =
  | { state: 'idle' }
  | { state: 'running'; startedAt: number }
  | { state: 'failed'; error: string; message: string; failedAt: number }
  | { state: 'done'; transcript: TranscriptDonePayload; finishedAt: number };

/** Kick off the transcription job. Server returns 202 immediately with
 *  the initial running state. The caller is expected to poll
 *  `getTranscribeStatus` until state is done or failed. */
export async function transcribeCallRecording(
  sessionId: SessionId,
  callId: string,
): Promise<TranscribeJobState> {
  return apiFetch<TranscribeJobState>(
    `/api/recordings/${encodeURIComponent(sessionId)}` +
      `/calls/${encodeURIComponent(callId)}/transcribe`,
    { method: 'POST' },
  );
}

/** Read the current job state. If the job has finished AND been pruned
 *  from memory, the server falls back to the persisted transcript row
 *  and returns it as { state: 'done' }. */
export async function getTranscribeStatus(
  sessionId: SessionId,
  callId: string,
): Promise<TranscribeJobState> {
  return apiFetch<TranscribeJobState>(
    `/api/recordings/${encodeURIComponent(sessionId)}` +
      `/calls/${encodeURIComponent(callId)}/transcribe-status`,
  );
}

/** Delete a single call recording. */
export async function deleteCallRecording(
  sessionId: SessionId,
  callId: string,
): Promise<void> {
  await apiFetch<null>(
    `/api/recordings/${encodeURIComponent(sessionId)}` +
      `/calls/${encodeURIComponent(callId)}`,
    { method: 'DELETE' },
  );
}

/** Nuclear option — delete every call in the session. */
export async function deleteRecording(sessionId: SessionId): Promise<void> {
  await apiFetch<null>(`/api/recordings/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
  useRecordingsStore.getState().remove(sessionId);
}

/** Refresh the store cache of which sessions have recordings on the server. */
export async function refreshRecordings(): Promise<void> {
  try {
    const res = await apiFetch<{ sessionIds: string[] }>('/api/recordings/sessions');
    useRecordingsStore.getState().setAll(res.sessionIds as SessionId[]);
  } catch {
    /* non-fatal */
  }
}
